import type { ProductListItemFragment } from "@/gql/graphql";
import type { PeptideProductCardData } from "./peptide-product-card";
import type { ProductCardData } from "@/ui/components/plp/product-card";
import { transformToProductCard } from "@/ui/components/plp/utils";
import { hasPeptideMetadata, parsePeptideMetadata } from "@/lib/peptide-metadata";
import { resolvePeptideStatus } from "@/lib/peptide-status";
import { getReferencePeptideForProductSlug } from "@/lib/research/get-reference-peptide-for-product";
import type { PeptideStatus } from "@/lib/research/types";

/**
 * Transform Saleor product to PeptideProductCardData (includes peptide metadata).
 */
export function transformToPeptideCard(
	product: ProductListItemFragment,
	locale: string,
	marketChannel: string,
): PeptideProductCardData {
	const base = transformToProductCard(product, locale, marketChannel);
	const peptideMetadata = parsePeptideMetadata(product.metadata);
	const referencePeptide = getReferencePeptideForProductSlug(product.slug);
	const resolvedStatus = resolvePeptideStatus(peptideMetadata?.availability, referencePeptide?.status);

	return {
		...base,
		peptideMetadata,
		resolvedStatus,
		peptideCode: referencePeptide?.code ?? product.slug,
	};
}

/**
 * Transform Saleor product to ProductCardData or PeptideProductCardData.
 * Uses PeptideProductCard when product has peptide metadata.
 */
export function transformToProductOrPeptideCard(
	product: ProductListItemFragment,
	locale: string,
	marketChannel: string,
): ProductCardData | PeptideProductCardData {
	if (hasPeptideMetadata(product.metadata)) {
		return transformToPeptideCard(product, locale, marketChannel);
	}
	return transformToProductCard(product, locale, marketChannel);
}

export type { PeptideStatus };
